import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit2, Check, X, RotateCcw } from 'lucide-react';

interface InlineFieldEditorProps {
  value: any;
  fieldType: 'money' | 'date' | 'number' | 'text' | 'uom';
  onSave: (newValue: any) => void;
  onRevert?: () => void;
  isEdited?: boolean;
  className?: string;
}

export function InlineFieldEditor({ 
  value, 
  fieldType, 
  onSave, 
  onRevert, 
  isEdited = false,
  className = '' 
}: InlineFieldEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const formatDisplayValue = (val: any) => {
    if (val === null || val === undefined || val === '') return '';
    
    if (fieldType === 'money') {
      const num = typeof val === 'string' ? parseFloat(val) : val;
      if (isNaN(num)) return val.toString();
      return `$${Math.abs(num).toFixed(2)}${num < 0 ? ' (Credit)' : ''}`;
    }
    
    if (fieldType === 'date' && typeof val === 'string') {
      try {
        return new Date(val).toISOString().split('T')[0];
      } catch {
        return val;
      }
    }
    
    return val.toString();
  };

  const validateValue = (val: string): { isValid: boolean; error: string; processedValue: any } => {
    if (val === '') return { isValid: true, error: '', processedValue: '' };
    
    switch (fieldType) {
      case 'money':
        const moneyNum = parseFloat(val.replace(/[$,]/g, ''));
        if (isNaN(moneyNum)) {
          return { isValid: false, error: 'Invalid money format', processedValue: val };
        }
        return { isValid: true, error: '', processedValue: moneyNum };
        
      case 'date':
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(val)) {
          return { isValid: false, error: 'Date must be YYYY-MM-DD', processedValue: val };
        }
        const date = new Date(val);
        if (isNaN(date.getTime())) {
          return { isValid: false, error: 'Invalid date', processedValue: val };
        }
        return { isValid: true, error: '', processedValue: val };
        
      case 'number':
        const num = parseFloat(val);
        if (isNaN(num)) {
          return { isValid: false, error: 'Must be a number', processedValue: val };
        }
        return { isValid: true, error: '', processedValue: num };
        
      default:
        return { isValid: true, error: '', processedValue: val };
    }
  };

  const handleStartEdit = () => {
    setEditValue(value?.toString() || '');
    setError('');
    setIsEditing(true);
  };

  const handleSave = () => {
    const validation = validateValue(editValue);
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }
    
    onSave(validation.processedValue);
    setIsEditing(false);
    setError('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError('');
    setEditValue('');
  };

  const handleRevert = () => {
    if (onRevert) {
      onRevert();
    }
    setIsEditing(false);
    setError('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type={fieldType === 'date' ? 'date' : fieldType === 'number' || fieldType === 'money' ? 'number' : 'text'}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className={`px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            error ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder={`Enter ${fieldType}...`}
        />
        <Button size="sm" variant="ghost" onClick={handleSave} className="h-6 w-6 p-0 text-green-600">
          <Check className="h-3 w-3" />
        </Button>
        <Button size="sm" variant="ghost" onClick={handleCancel} className="h-6 w-6 p-0 text-gray-500">
          <X className="h-3 w-3" />
        </Button>
        {error && (
          <span className="text-xs text-red-500">{error}</span>
        )}
      </div>
    );
  }

  return (
    <div className={`group flex items-center gap-2 ${className}`}>
      <span className="text-sm text-gray-600 font-mono">
        {formatDisplayValue(value) || '-'}
      </span>
      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          size="sm"
          variant="ghost"
          onClick={handleStartEdit}
          className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
        >
          <Edit2 className="h-3 w-3" />
        </Button>
        {isEdited && onRevert && (
          <Button
            size="sm"
            variant="ghost"
            onClick={handleRevert}
            className="h-6 w-6 p-0 text-orange-500 hover:text-orange-700"
            title="Revert to original"
          >
            <RotateCcw className="h-3 w-3" />
          </Button>
        )}
      </div>
      {isEdited && (
        <Badge variant="secondary" className="text-xs">
          edited
        </Badge>
      )}
    </div>
  );
}
